/**
 * The Rule Element kinds this engine implements. Build order (docs/design.md): FlatModifier,
 * ActiveEffectLike (#5) -> GrantItem, ChoiceSet (#6) -> MartialProficiency, Sense, BaseSpeed (#7)
 * -> Strike, DamageDice and every remaining sheet-affecting kind (#8). Canvas-only kinds are
 * deliberate no-ops, listed in CANVAS_ONLY_KEYS.
 */
import { CANVAS_ONLY_KEYS, FOUNDRY_RULE_ELEMENT_KEYS } from "../values";
import { ActiveEffectLikeHandler } from "./ae-like";
import { ActorTraitsHandler } from "./actor-traits";
import { AdjustDegreeOfSuccessHandler } from "./adjust-degree-of-success";
import { AdjustModifierHandler } from "./adjust-modifier";
import { AdjustStrikeHandler } from "./adjust-strike";
import { AuraHandler } from "./aura";
import type { RuleHandler } from "./base";
import { BaseSpeedHandler } from "./base-speed";
import { BattleFormHandler } from "./battle-form";
import { ChoiceSetHandler } from "./choice-set";
import { CraftingAbilityHandler } from "./crafting-ability";
import { CreatureSizeHandler } from "./creature-size";
import { CriticalSpecializationHandler } from "./critical-specialization";
import { DamageAlterationHandler } from "./damage-alteration";
import { DamageDiceHandler } from "./damage-dice";
import { EphemeralEffectHandler } from "./ephemeral-effect";
import { FlatModifierHandler } from "./flat-modifier";
import { GrantItemHandler } from "./grant-item";
import { ItemAlterationHandler } from "./item-alteration";
import { ImmunityHandler, ResistanceHandler, WeaknessHandler } from "./iwr";
import { MartialProficiencyHandler } from "./martial-proficiency";
import { MultipleAttackPenaltyHandler } from "./multiple-attack-penalty";
import { NoteHandler } from "./note";
import { DexterityModifierCapHandler, FastHealingHandler, LoseHitPointsHandler, SpecialResourceHandler, TempHPHandler } from "./resources";
import { RollOptionHandler } from "./roll-option";
import { RollTwiceHandler } from "./roll-twice";
import { SenseHandler } from "./sense";
import { SpecialStatisticHandler } from "./special-statistic";
import { StrikeHandler } from "./strike";
import { SubstituteRollHandler } from "./substitute-roll";

export const HANDLERS: Readonly<Record<string, RuleHandler>> = Object.freeze({
  ActiveEffectLike: ActiveEffectLikeHandler,
  ActorTraits: ActorTraitsHandler,
  AdjustDegreeOfSuccess: AdjustDegreeOfSuccessHandler,
  AdjustModifier: AdjustModifierHandler,
  AdjustStrike: AdjustStrikeHandler,
  Aura: AuraHandler,
  BaseSpeed: BaseSpeedHandler,
  BattleForm: BattleFormHandler,
  ChoiceSet: ChoiceSetHandler,
  CraftingAbility: CraftingAbilityHandler,
  CreatureSize: CreatureSizeHandler,
  CriticalSpecialization: CriticalSpecializationHandler,
  DamageAlteration: DamageAlterationHandler,
  DamageDice: DamageDiceHandler,
  DexterityModifierCap: DexterityModifierCapHandler,
  EphemeralEffect: EphemeralEffectHandler,
  FastHealing: FastHealingHandler,
  FlatModifier: FlatModifierHandler,
  GrantItem: GrantItemHandler,
  Immunity: ImmunityHandler,
  ItemAlteration: ItemAlterationHandler,
  LoseHitPoints: LoseHitPointsHandler,
  MartialProficiency: MartialProficiencyHandler,
  MultipleAttackPenalty: MultipleAttackPenaltyHandler,
  Note: NoteHandler,
  Resistance: ResistanceHandler,
  RollOption: RollOptionHandler,
  RollTwice: RollTwiceHandler,
  Sense: SenseHandler,
  SpecialResource: SpecialResourceHandler,
  SpecialStatistic: SpecialStatisticHandler,
  Strike: StrikeHandler,
  SubstituteRoll: SubstituteRollHandler,
  TempHP: TempHPHandler,
  Weakness: WeaknessHandler,
});

/** `stopped`: an implemented kind the engine refused to follow (a grant chain too deep or circular). */
export type RuleSupport = "implemented" | "canvas-only" | "unsupported" | "unknown" | "stopped";

export function classifyKey(key: string): RuleSupport {
  if (key in HANDLERS) return "implemented";
  if ((CANVAS_ONLY_KEYS as readonly string[]).includes(key)) return "canvas-only";
  if ((FOUNDRY_RULE_ELEMENT_KEYS as readonly string[]).includes(key)) return "unsupported";
  return "unknown";
}
